import {
  type ExposureOrGroup,
  type ExposureOrGroupActionPayload,
  type Exposures,
  type Groups,
  type IAddExposureOrGroup,
  type IDeleteExposureOrGroup,
  type IUpdateExposureOrGroup,
  useAddExposure,
  useAddGroupField,
  useDeleteExposure,
  useDeleteGroupField,
  useSubmission,
  useUpdateExposure,
  useUpdateGroupField,
} from 'api/submission';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';

import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';

type Props =
  | {
      type: 'exposure';
      name: Exposures;
    }
  | {
      type: 'group';
      name: Groups;
    };

/**
 * Custom hook that manages exposures and group fields for a specific submission.
 *
 * @template T - The type of the resoruce data.
 * @param name - The name of the resource.
 */
const useExposuresOrGroupFields = <T>({ name, type }: Props) => {
  const { id } = useParams() as { id: string };
  const { setFullscreenLoading } = useFullscreenLoader();

  const { data: submission } = useSubmission(id);

  const { mutateAsync: addExposure } = useAddExposure(id);
  const { mutateAsync: updateExposure } = useUpdateExposure(id);
  const { mutateAsync: deleteExposure } = useDeleteExposure(id);

  const { mutateAsync: addGroupField } = useAddGroupField(id);
  const { mutateAsync: updateGroupField } = useUpdateGroupField(id);
  const { mutateAsync: deleteGroupField } = useDeleteGroupField(id);

  const relatedResource = (
    type === 'exposure' ? submission?.data.exposures[name] : submission?.data.groups[name]
  ) as ExposureOrGroup<T>[];

  /**
   * Performs an exposure or group-field-related action such as adding, updating, or deleting.
   *
   * @param payload - The payload containing action type, data, and loading text.
   */
  const performAction = async (payload: ExposureOrGroupActionPayload) => {
    const { action, loadingText, displayBEMessage = true } = payload;

    try {
      setFullscreenLoading(true, loadingText);

      if (action === 'add') {
        const { data } = payload;

        const body: IAddExposureOrGroup = {
          submissionId: id,
          body: {
            name,
            data,
          },
        };

        if (type === 'exposure') {
          await addExposure(body);
        } else {
          await addGroupField(body);
        }
      } else if (action === 'update') {
        const { data, resourceId } = payload;

        const body: IUpdateExposureOrGroup = {
          submissionId: id,
          body: {
            id: resourceId,
            name,
            data,
          },
        };

        if (type === 'exposure') {
          await updateExposure(body);
        } else {
          await updateGroupField(body);
        }
      } else {
        const { resourceId } = payload;

        const body: IDeleteExposureOrGroup = {
          submissionId: id,
          body: {
            id: resourceId,
            name,
          },
        };

        if (type === 'exposure') {
          await deleteExposure(body);
        } else {
          await deleteGroupField(body);
        }
      }
    } catch (error) {
      if (displayBEMessage) displayBackendErrorMessage(error);
      throw error;
    } finally {
      setFullscreenLoading(false);
    }
  };

  return {
    submissionId: id,
    submission,
    relatedResource,
    addGroupField,
    updateGroupField,
    deleteGroupField,
    addExposure,
    updateExposure,
    deleteExposure,
    performAction,
  };
};

export default useExposuresOrGroupFields;
